import { useTranslation } from "react-i18next";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { useNavigate } from "react-router-dom";

import NewPage from "../../../layouts/page/NewPage";
import Form from "./Form";
import MenuConfig from "../../../../constants/MenuConfig";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { successAlert } from "../../../components/alert/defaultAlert";
import axiosInstance from "../../../../services/AxiosInstance";
import { convertStringToStringDouble } from "../../../../utils/ConverterUtils";
import { getId } from "../../../../utils/ApiUtils";
import { formatDateToYYYYMMDD } from "../../../../utils/DateUtils";
import { useBranch } from "../../../../context/BranchContext";

const AdvanceNew = () => {

    const { t } = useTranslation();
    const { selectedBranch } = useBranch();
    const [resetForm, setResetForm] = useState(false);
    const [entityData, setEntityData] = useState(null);
    const navigate = useNavigate();

    // A data de hoje é criada uma vez e congelada no estado. Com enableReinitialize ligado,
    // o Formik compara os initialValues a cada render: um new Date() aqui dentro nasce com
    // milissegundos diferentes toda vez, o form era reinicializado sem parar e apagava o
    // valor enquanto o operador digitava.
    const [today] = useState(() => new Date());

    // A data já nasce com hoje: o caso comum é registrar o vale na hora em que entrega o
    // dinheiro, e obrigar a escolher a data em toda entrega seria atrito à toa.
    const getInitialValues = () => ({
        employee: null,
        value: '',
        advanceDate: today,
        description: '',
        // Mantém a filial do contexto depois de salvar e limpar o formulário.
        branch: selectedBranch ? { value: selectedBranch.id, label: selectedBranch.name } : null
    });

    const formSchema = Yup.object().shape({
        employee: Yup.object().required(t('validation.required.employee')).nullable(),
        value: Yup.number()
            .typeError(t('validation.required.value'))
            .positive(t('validation.required.value'))
            .required(t('validation.required.value')),
        advanceDate: Yup.string().required(t('validation.required.date')).nullable(),
        branch: Yup.object().required(t('validation.required.branch')).nullable(),
        description: Yup.string().max(255, t('validation.max_length', { max: 255 }))
    });

    const formik = useFormik({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        validationSchema: formSchema,
        onSubmit: (values) => {
            const payload = {
                id: getId(entityData),
                employee: { id: values.employee.value },
                value: convertStringToStringDouble(values.value),
                // A data vai como texto no fuso de quem lançou. Mandando o Date cru, o
                // JSON sai em UTC (toJSON) e um vale lançado à noite chega ao servidor já
                // no dia seguinte: ele gravava com a data errada e sumia da lista, que
                // filtra até hoje. Foi assim que "salvou com sucesso e não apareceu".
                advanceDate: formatDateToYYYYMMDD(values.advanceDate),
                description: values.description,
                branch: { id: values.branch.value }
            };

            axiosInstance.post(Api.cash_flow.advance, payload)
                .then(() => {
                    successAlert(t('message.operation_successfully'), () => {
                        if (entityData == null) {
                            onReset();
                        } else {
                            navigate(MenuConfig.cash_flow.advance.route);
                        }
                    });
                })
                .catch(error => ApiError(error))
                .finally(() => formik.setSubmitting(false));
        }
    });

    const onReset = () => {
        // resetForm puro limparia a filial do contexto e quebraria o lançamento seguinte,
        // que é o caso normal aqui: o gestor costuma registrar vários vales de uma vez.
        formik.resetForm({ values: getInitialValues() });
        setResetForm((current) => !current);
    }

    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    return (
        <>
            <NewPage
                motherMenu={t('cash_flow.label')}
                childMenu={t('cash_flow.advances')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        entityData={entityData}
                        resetForm={resetForm}
                    />
                }
                setEntityData={setEntityData}
                path={Api.cash_flow.advance}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.cash_flow.advance.route}
            />
        </>
    );
}

export default AdvanceNew;
